///
/// EarthBrowser
///
/// Created by Matt Giger on 11/4/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Toasts CrashRecovery shows when the previous sessions formed a crash
/// loop (design-system Toast Pattern, warning variant). Level 1 is silent:
/// its layers come back by themselves within seconds. Level 2 tells the
/// user what is happening and offers to skip the suspected layer; level 3
/// starts without the heavy layers and offers to bring them back.

import { TriangleAlert } from "lucide-react"
import { ToastAction } from "@/components/ui/toast"
import { toast } from "@/components/ui/use-toast"

const cIcon = (
	<div className="p-2 rounded-lg bg-amber-500/15 text-amber-400">
		<TriangleAlert className="h-4 w-4" />
	</div>
)

/** Level 2: layers are being restored in lite mode; the suspect can be skipped */
export function showRecoveringToast(suspectName: string | null, onSkipSuspect: () => void): void {
	toast({
		variant: "warning",
		icon: cIcon,
		title: "Recovering from a crash",
		description: suspectName
			? `EarthBrowser restarted unexpectedly. Layers are coming back one at a time in reduced-memory mode; ${suspectName} may be the cause.`
			: "EarthBrowser restarted unexpectedly. Layers are coming back one at a time in reduced-memory mode.",
		duration: 12000,
		action: suspectName
			? <ToastAction altText={`Skip ${suspectName}`} onClick={onSkipSuspect}>Skip {suspectName}</ToastAction>
			: undefined,
	})
}

/** Level 3: heavy layers were left off; one tap restores them (in lite mode) */
export function showStartLightToast(layerCount: number, suspectName: string | null, onRestore: () => void): void {
	const layers = layerCount === 1 ? "1 layer was" : `${layerCount} layers were`
	toast({
		variant: "warning",
		icon: cIcon,
		title: "Started without heavy layers",
		description: suspectName
			? `EarthBrowser crashed repeatedly, likely from ${suspectName}. ${layers} left off. Restore in reduced-memory mode?`
			: `EarthBrowser crashed repeatedly. ${layers} left off. Restore in reduced-memory mode?`,
		duration: 20000,
		action: <ToastAction altText="Restore layers" onClick={onRestore}>Restore layers</ToastAction>,
	})
}
